export type McWasmExports = {
  getBootstrapKeyBase64: () => string;
  initializeSessionFromPlaintext: (plaintextBase64: string) => string;
  submitAnswer: (questionIdStr: string, answerIdStr: string) => string;
  getPlayState: () => string;
  buildFinishPayload: () => string;
  disposeSession: () => void;
};

type Fn = (...args: unknown[]) => unknown;

function pickMethod(obj: Record<string, unknown>, ...names: string[]): Fn | undefined {
  for (const name of names) {
    const fn = obj[name];
    if (typeof fn === 'function') return fn as Fn;
  }
  return undefined;
}

function wrapRawExports(raw: Record<string, unknown>): McWasmExports | null {
  const getBootstrapKeyBase64 = pickMethod(raw, 'GetBootstrapKeyBase64', 'getBootstrapKeyBase64');
  const initializeSessionFromPlaintext = pickMethod(
    raw,
    'InitializeSessionFromPlaintext',
    'initializeSessionFromPlaintext'
  );
  const submitAnswer = pickMethod(raw, 'SubmitAnswer', 'submitAnswer');
  const getPlayState = pickMethod(raw, 'GetPlayState', 'getPlayState');
  const buildFinishPayload = pickMethod(raw, 'BuildFinishPayload', 'buildFinishPayload');
  const disposeSession = pickMethod(raw, 'DisposeSession', 'disposeSession');

  if (
    !getBootstrapKeyBase64 ||
    !initializeSessionFromPlaintext ||
    !submitAnswer ||
    !getPlayState ||
    !buildFinishPayload ||
    !disposeSession
  ) {
    return null;
  }

  return {
    getBootstrapKeyBase64: () => getBootstrapKeyBase64() as string,
    initializeSessionFromPlaintext: (plaintextBase64: string) =>
      initializeSessionFromPlaintext(plaintextBase64) as string,
    submitAnswer: (questionIdStr: string, answerIdStr: string) =>
      submitAnswer(questionIdStr, answerIdStr) as string,
    getPlayState: () => getPlayState() as string,
    buildFinishPayload: () => buildFinishPayload() as string,
    disposeSession: () => {
      disposeSession();
    },
  };
}

function deepFindWasmExports(node: unknown, depth = 0): McWasmExports | null {
  if (!node || typeof node !== 'object' || depth > 14) return null;

  const wrapped = wrapRawExports(node as Record<string, unknown>);
  if (wrapped) return wrapped;

  for (const value of Object.values(node as Record<string, unknown>)) {
    if (typeof value === 'function') continue;
    const found = deepFindWasmExports(value, depth + 1);
    if (found) return found;
  }

  return null;
}

const EXPORT_PATHS: string[][] = [
  ['Quizter', 'Mc', 'Wasm', 'McWasmExports'],
  ['Mc', 'Wasm', 'McWasmExports'],
  ['McWasmExports'],
];

function resolvePath(root: Record<string, unknown>, path: string[]): unknown {
  let cur: unknown = root;
  for (const segment of path) {
    if (!cur || typeof cur !== 'object') return undefined;
    cur = (cur as Record<string, unknown>)[segment];
  }
  return cur;
}

export function resolveMcWasmExports(
  assemblyRoot: Record<string, unknown>
): McWasmExports {
  for (const path of EXPORT_PATHS) {
    const candidate = resolvePath(assemblyRoot, path);
    const wrapped = candidate && typeof candidate === 'object' ? wrapRawExports(candidate as Record<string, unknown>) : null;
    if (wrapped) return wrapped;
  }

  const deep = deepFindWasmExports(assemblyRoot);
  if (deep) return deep;

  const topKeys = Object.keys(assemblyRoot).join(', ') || '(empty)';
  throw new Error(
    `MC WASM: engine exports not found. Assembly keys: ${topKeys}`
  );
}

export async function loadMcAssemblyExports(
  getAssemblyExports: (name: string) => Promise<Record<string, unknown>>,
  mainAssemblyName: string
): Promise<McWasmExports> {
  const names = [
    mainAssemblyName,
    mainAssemblyName.replace(/\.dll$/i, '.wasm'),
    'Quizter.Mc.Wasm.wasm',
    'Quizter.Mc.Wasm.dll',
    'Quizter.Mc.Wasm',
  ];
  const tried = new Set<string>();

  for (const name of names) {
    if (!name || tried.has(name)) continue;
    tried.add(name);
    try {
      const assembly = await getAssemblyExports(name);
      if (assembly && typeof assembly === 'object') {
        return resolveMcWasmExports(assembly);
      }
    } catch {
      // try next assembly name
    }
  }

  throw new Error(
    'MC WASM: could not load assembly exports. Hard-refresh the browser.'
  );
}
